import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { GET_USER_FRANCHISES_QUERY } from "@/graphql/auth";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";

/**
 * Hook to handle loading franchise access data when the application initializes.
 * This ensures that franchise data is available throughout the application lifecycle.
 *
 * IMPORTANT: This hook should only be used once at the app level (AppContainer).
 * Child components should read from the salon store, not call this hook again.
 */
export function useFranchiseLoader() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const hasAttemptedLoadRef = useRef(false);
  const isLoadingRef = useRef(false);

  // Auth state
  const { isAuthenticated } = useAuthStore();

  // Salon state
  const { franchiseAccess, setFranchiseAccess, setLoading } = useSalonStore();

  // GraphQL query - Apollo Client automatically handles auth tokens via authContextLink
  const [getUserFranchisesQuery] = useLazyQuery(GET_USER_FRANCHISES_QUERY);

  const loadFranchiseAccess = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    if (isLoadingRef.current) {
      return;
    }
    isLoadingRef.current = true;
    setIsLoading(true);
    setLoading(true);
    setError(null);

    try {
      const result = await getUserFranchisesQuery({
        fetchPolicy: "network-only", // Always fetch fresh data from server
      });

      if (!result.data?.getUserFranchises) {
        throw new Error("Failed to load franchise access data");
      }

      const franchiseAccessData = result.data.getUserFranchises;
      setFranchiseAccess(franchiseAccessData);
    } catch (err) {
      console.error("[FranchiseLoader] Failed to load franchise access:", err);
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);

      // Show user-friendly error message
      toast.error("Failed to load salon access", {
        description: "Unable to load your salon access information. Please refresh the page.",
      });
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
      setLoading(false);
    }
  }, [getUserFranchisesQuery, isAuthenticated, setFranchiseAccess, setLoading]);

  // Load franchise data once when user is authenticated
  useEffect(() => {
    if (isAuthenticated && !hasAttemptedLoadRef.current) {
      hasAttemptedLoadRef.current = true;
      loadFranchiseAccess();
    }
  }, [isAuthenticated, loadFranchiseAccess]);

  return {
    isLoading,
    error,
    loadFranchiseAccess,
    hasData: franchiseAccess.length > 0,
  };
}
